import React, { useEffect, useState, useCallback, useRef } from 'react';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { 
  IconButton, 
  Tooltip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Box,
  Button,
  Typography,
  keyframes
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useOrderNotificationSSE } from '../hooks/useOrderNotificationSSE';

interface OrderNotificationProps {
  storeId: string;
  clerkId: string | undefined;
  onNotificationMessages: (storeId: string, activeStoreId: string, activeDiningTableId: string, notificationType: string, activeOrderId?: string) => void;
}

const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(5px); }
  50% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

const OrderNotification: React.FC<OrderNotificationProps> = ({ storeId, clerkId, onNotificationMessages }) => {
  const notification = useOrderNotificationSSE({ url: `${process.env.REACT_APP_API_BASE_URL}/notifications/stores/${storeId}/clerks/${clerkId}`, storeId, clerkId });

  // 通知データから一意の識別子を生成（ActiveOrderIdを含む）
  const generateNotificationKey = useCallback((notification: { 
    storeId: string; 
    activeStoreId: string; 
    activeDiningTableId: string; 
    notificationType: string; 
    activeOrderId?: string; // オプショナルとして定義
  }) => {
    // ActiveOrderIdを含めた一意の識別子を生成
    // activeOrderIdがない場合はactiveStoreIdを使用
    const activeOrderId = notification.activeOrderId || notification.activeStoreId;
    return `${notification.storeId}-${notification.activeStoreId}-${notification.activeDiningTableId}-${notification.notificationType}-${activeOrderId}`;
  }, []);

  // 処理済み通知を追跡するための状態
  const [processedNotifications, setProcessedNotifications] = useState<Set<string>>(new Set());
  
  // 最後に処理した通知のインデックス
  const lastProcessedIndexRef = useRef<number>(-1);
  
  useEffect(() => {
    // 通知データが存在する場合のみ処理
    if (notification.orderNotificationData.length > 0) {
      // 最新の通知を取得
      const latestNotification = notification.orderNotificationData[notification.orderNotificationData.length - 1];
      
      if (latestNotification && notification.orderNotificationData.length > lastProcessedIndexRef.current + 1) {
        // 通知の一意の識別子を生成
        const notificationKey = generateNotificationKey(latestNotification);
        
        // まだ処理されていない通知の場合のみ処理
        if (!processedNotifications.has(notificationKey)) {
          console.log('Processing notification in OrderNotification:', latestNotification);
          
          // 親コンポーネントに通知を伝達（activeOrderIdを含む）
          onNotificationMessages(
            latestNotification.storeId,
            latestNotification.activeStoreId,
            latestNotification.activeDiningTableId,
            latestNotification.notificationType,
            latestNotification.activeOrderId
          );
          
          // 処理済み通知のセットを更新
          setProcessedNotifications(prev => {
            const newSet = new Set(prev);
            newSet.add(notificationKey);
            return newSet;
          });
          
          console.log('Generated notification key:', notificationKey);
        } else {
          console.log('Notification already processed, skipping:', notificationKey);
        }
        
        // 最後に処理した通知のインデックスを更新
        lastProcessedIndexRef.current = notification.orderNotificationData.length - 1;
      }
    }
  }, [
    notification.orderNotificationData.length, 
    notification.orderNotificationData,
    onNotificationMessages, 
    generateNotificationKey,
    processedNotifications
  ]);

  const combinedNotificationShake = notification.notificationShake;
  const combinedNotificationColor = notification.notificationColor;
  const combinedNotificationMessages = notification.notificationMessages;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton 
        onClick={notification.audioAllowed ? notification.lockAudio : notification.unlockAudio}
        sx={{ 
          fontSize: '36px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {!notification.audioAllowed ? (
          <VolumeOffIcon fontSize="inherit" color="action" />
        ) : (
          <VolumeUpIcon fontSize="inherit" color="primary" />
        )}
      </IconButton>

      <Tooltip 
        title={
          <Box sx={{ p: 1 }}>
            {combinedNotificationMessages.map((msg: any, index: number) => (
              <Typography key={index} variant="body2">
                {msg.time} - {msg.message}
              </Typography>
            ))}
          </Box>
        } 
        arrow
      >
        <IconButton 
          onClick={notification.openNotificationDialog}
          sx={{ 
            fontSize: '36px',
            animation: combinedNotificationShake ? `${shakeAnimation} 0.5s ease-in-out` : 'none',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <NotificationsIcon 
            sx={{ 
              fontSize: 'inherit', 
              color: combinedNotificationColor,
              transition: 'color 0.3s ease',
            }} 
          />
        </IconButton>
      </Tooltip>

      <Dialog 
        open={notification.notificationDialogOpen} 
        onClose={notification.closeNotificationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2
        }}>
          <Typography variant="h6">通知履歴</Typography>
          <IconButton 
            onClick={notification.closeNotificationDialog}
            size="small"
            sx={{ 
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>時間</TableCell>
                  <TableCell>メッセージ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinedNotificationMessages.map((msg: any, index: number) => (
                  <TableRow key={index} hover>
                    <TableCell>{msg.time}</TableCell>
                    <TableCell>{msg.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button 
            onClick={notification.closeNotificationDialog}
            variant="contained"
            color="primary"
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderNotification;
